import { AuthErrorCode, type AuthError } from '../types/auth';
import { translateError, formatErrorForDisplay } from './translateError';

// Re-export translateError and formatErrorForDisplay for convenience
export { translateError, formatErrorForDisplay };

/**
 * Convert Firebase error to AuthError with Ukrainian message
 */
export function handleFirebaseError(error: unknown): AuthError {
  // DEBUG: Log error structure for INTERNAL errors
  if (error && typeof error === 'object') {
    const err = error as { code?: string; message?: string; details?: unknown; response?: unknown };
    if (err.code === 'INTERNAL' || err.code === 'internal') {
      console.log('[DEBUG handleFirebaseError] INTERNAL error detected:', {
        code: err.code,
        message: err.message,
        details: err.details,
        response: err.response,
        fullError: JSON.stringify(error, null, 2)
      });
    }
  }
  
  // Use the translateError function for better error handling
  const translatedMessage = translateError(error as object | Error | string | null | undefined);
  
  let code: string = AuthErrorCode.UNKNOWN_ERROR;
  
  if (error && typeof error === 'object') {
    const firebaseError = error as { code?: string };
    code = firebaseError.code || AuthErrorCode.UNKNOWN_ERROR;
  }

  return {
    code: code as AuthErrorCode,
    message: translatedMessage,
    originalError: error instanceof Error ? error : undefined
  };
}

/**
 * Handle auth error - alias for handleFirebaseError
 */
export function handleAuthError(error: unknown): AuthError {
  return handleFirebaseError(error);
}

/**
 * Create custom auth error with Ukrainian message
 */
export function createAuthError(code: AuthErrorCode, customMessage?: string): AuthError {
  // Use translateError if no custom message provided
  const message = customMessage || translateError(code);
  
  return {
    code,
    message,
    originalError: undefined
  };
}

/**
 * Check if error requires re-authentication
 */
export function requiresReauthentication(error: AuthError): boolean {
  return error.code === AuthErrorCode.REQUIRES_RECENT_LOGIN;
}

/**
 * Get human-readable error message (legacy function, use translateError instead)
 */
export function getErrorMessage(error: unknown): string {
  return formatErrorForDisplay(error);
}

/**
 * AuthError class for throwing errors with Ukrainian messages
 */
export class AuthErrorClass extends Error {
  code: AuthErrorCode | string;
  originalError?: Error;

  constructor(code: AuthErrorCode | string, message?: string, originalError?: Error) {
    // Use translateError if no custom message provided
    const errorMessage = message || translateError(code);
    super(errorMessage);
    this.name = 'AuthError';
    this.code = code;
    this.originalError = originalError;
  }
}

/**
 * Helper to extract error message from unknown error type
 * Always returns Ukrainian message
 */
export function extractErrorMessage(error: unknown): string {
  return formatErrorForDisplay(error);
}

/**
 * Helper to check if error is a Firebase auth error
 */
export function isFirebaseAuthError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  
  const err = error as { code?: string };
  return err.code?.startsWith('auth/') ?? false;
}

/**
 * Helper to check if error is a Firestore error
 */
export function isFirestoreError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  
  const err = error as { code?: string };
  const firestoreCodes = ['permission-denied', 'not-found', 'already-exists', 'unauthenticated', 'unavailable'];
  return firestoreCodes.includes(err.code || '');
}

/**
 * Safely handle async errors and return Ukrainian message
 */
export async function handleAsyncError<T>(
  promise: Promise<T>,
  customErrorMessage?: string
): Promise<{ data: T | null; error: AuthError | null }> {
  try {
    const data = await promise;
    return { data, error: null };
  } catch (error) {
    const authError = handleFirebaseError(error);
    if (customErrorMessage) {
      authError.message = customErrorMessage;
    }
    return { data: null, error: authError };
  }
}
